import { type ReactNode, useEffect, useRef } from 'react';

import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

import styles from './ScrollList.module.css';

export interface ScrollListProps {
  children: ReactNode;
  /** More rows exist on the server. */
  hasMore?: boolean;
  /** The next rows are on their way. */
  isLoadingMore?: boolean;
  /** Asked for the next rows when the bottom of the list scrolls into view. */
  onLoadMore?: (() => void) | undefined;
  className?: string | undefined;
}

/**
 * A list inside a card that scrolls on its own past `--card-list-height`, so a busy list never
 * makes the card — or the page — longer. With `onLoadMore` it is an infinite list: reaching the
 * bottom loads the next rows.
 */
export function ScrollList({
  children,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  className,
}: ScrollListProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const end = endRef.current;
    if (!root || !end || !hasMore || isLoadingMore || !onLoadMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      // Starts loading a little before the very bottom, so scrolling rarely has to wait.
      { root, rootMargin: '0px 0px 120px 0px' },
    );
    observer.observe(end);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, onLoadMore]);

  return (
    <div ref={rootRef} className={cn(styles.scroll, className)}>
      {children}
      <div ref={endRef} className={styles.end} aria-hidden="true" />
      {isLoadingMore && (
        <div className={styles.more}>
          <Spinner size="sm" label="Loading more" />
        </div>
      )}
    </div>
  );
}
